import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { clearWorkspaceTabChartState } from 'features/workspaceTabs/utils/clearWorkspaceTabChartState'
import type { OpenDocumentTabOptions } from 'features/workspaceTabs/utils/resourceTabs'
import { scheduleAfterPaint } from 'features/workspaceTabs/utils/scheduleAfterPaint'
import {
  createFreshTabFor,
  type DestinationTabOptions,
  findTabShowingTarget,
  isDocumentsViewerItem,
  type NavigateActiveTabOptions,
  pushDocumentOntoTab,
  resourceKeyForNavigation,
} from 'features/workspaceTabs/utils/targetTabs'
import { workspaceScopeMode } from 'features/workspaceTabs/utils/workspaceScope'
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
} from 'react'
import { flushSync } from 'react-dom'

type NavigateTab = (options: NavigateActiveTabOptions) => void

export interface WorkspaceTabNavigation {
  navigateActiveTab: NavigateTab
  openNewTab: NavigateTab
}

/**
 * The palette's two ways of putting a target on screen: `navigateActiveTab`
 * reuses or replaces the active tab, `openNewTab` always appends.
 */
export function useWorkspaceTabNavigation({
  siteId,
  openTabsRef,
  activeTabIdRef,
  scopeRef,
  setOpenTabs,
  setActiveTabId,
  confirmLeaveActiveTab,
  navigateToTab,
  activateWorkspaceTab,
  focusTabAndNavigate,
  openDestinationTab,
  openDocumentTab,
}: {
  siteId: string
  openTabsRef: MutableRefObject<DocumentTab[]>
  activeTabIdRef: MutableRefObject<string>
  scopeRef: MutableRefObject<string>
  setOpenTabs: Dispatch<SetStateAction<DocumentTab[]>>
  setActiveTabId: Dispatch<SetStateAction<string>>
  confirmLeaveActiveTab: () => boolean
  navigateToTab: (tab: DocumentTab) => void
  activateWorkspaceTab: (tabId: string) => void
  focusTabAndNavigate: (
    tabId: string,
    item: { label?: string; href: string }
  ) => void
  openDestinationTab: (options: DestinationTabOptions) => void
  openDocumentTab: (options: OpenDocumentTabOptions) => void
}): WorkspaceTabNavigation {
  const navigateActiveTab = useCallback<NavigateTab>(
    (options) => {
      const currentTabs = openTabsRef.current
      const activeIndex = currentTabs.findIndex(
        (tab) => tab.id === activeTabIdRef.current
      )
      const activeTab = currentTabs[activeIndex]
      if (!activeTab) {
        if (options.kind === 'document') openDocumentTab(options)
        else openDestinationTab(options)
        return
      }

      const resourceKey = resourceKeyForNavigation(
        options,
        workspaceScopeMode(scopeRef.current)
      )
      const owner = findTabShowingTarget(
        currentTabs.filter((tab) => tab.id !== activeTab.id),
        options,
        resourceKey
      )
      if (owner) {
        if (options.kind === 'document') activateWorkspaceTab(owner.id)
        else focusTabAndNavigate(owner.id, options)
        return
      }

      if (!confirmLeaveActiveTab()) return

      // Every conversion except pushing a document onto a viewer tab gets a
      // fresh tab id, so effects the outgoing page bound to the old tab
      // (adopting it as their destination) cannot rewrite the new one.
      const isInDocumentsViewer = isDocumentsViewerItem(
        activeTab.history[activeTab.currentIndex]
      )
      const nextTab =
        options.kind === 'document' && isInDocumentsViewer
          ? pushDocumentOntoTab(activeTab, options, resourceKey)
          : createFreshTabFor(options, resourceKey)
      if (nextTab.id !== activeTab.id) {
        clearWorkspaceTabChartState(siteId, activeTab.id)
      }

      // Leaving the documents viewer: navigate before swapping the tab. While
      // mounted, the documents page reconciles its URL against the open tabs,
      // and with its own tab already replaced it would append a new document tab.
      const leavesDocumentsViewer =
        isInDocumentsViewer &&
        nextTab.history[nextTab.currentIndex].type === 'destination'
      if (leavesDocumentsViewer) navigateToTab(nextTab)

      flushSync(() => {
        setOpenTabs((tabs) =>
          tabs.map((tab, index) => (index === activeIndex ? nextTab : tab))
        )
        setActiveTabId(nextTab.id)
      })
      if (!leavesDocumentsViewer) {
        scheduleAfterPaint(() => navigateToTab(nextTab))
      }
    },
    [
      activateWorkspaceTab,
      activeTabIdRef,
      confirmLeaveActiveTab,
      focusTabAndNavigate,
      navigateToTab,
      openDestinationTab,
      openDocumentTab,
      openTabsRef,
      scopeRef,
      setActiveTabId,
      setOpenTabs,
      siteId,
    ]
  )

  const openNewTab = useCallback<NavigateTab>(
    (options) => {
      if (!confirmLeaveActiveTab()) return
      const wantedKey = resourceKeyForNavigation(
        options,
        workspaceScopeMode(scopeRef.current)
      )
      // Identities stay unique: a second tab for the same target carries none,
      // so other openers keep reusing the first.
      const isTaken = openTabsRef.current.some(
        (tab) => tab.resourceKey === wantedKey
      )
      const tab = createFreshTabFor(options, isTaken ? undefined : wantedKey)
      flushSync(() => {
        setOpenTabs((tabs) => [...tabs, tab])
        setActiveTabId(tab.id)
      })
      scheduleAfterPaint(() => navigateToTab(tab))
    },
    [
      confirmLeaveActiveTab,
      navigateToTab,
      openTabsRef,
      scopeRef,
      setActiveTabId,
      setOpenTabs,
    ]
  )

  return { navigateActiveTab, openNewTab }
}
